const BASE_URL = `${process.env.REACT_APP_API_URL}/api`;

/**
 * Centralized fetch helper
 */
const apiFetch = async (
  endpoint: string,
  idToken: string | null | undefined,
  options: RequestInit = {}
) => {
  if (!idToken) throw new Error("No ID token available");

  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`API Error ${res.status}: ${errorText}`);
  }

  return res.json();
};

/**
 * Utility to build query strings
 */
const buildQuery = (params: Record<string, string | undefined>) => {
  return (
    "?" +
    Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value!)}`
      )
      .join("&")
  );
};

// GA
const getGaData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/ga/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// WooCommerce
const getWooData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/woo/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// Campaigns
const getCampaignsData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/campaign/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// WordPress
const getWordpressData = async (siteId: string, idToken: string | null) => {
  return apiFetch(`/wordpress/${siteId}`, idToken);
};

// SendGrid
const getSendGridData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/sendgrid/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// Google Search Console
const getGoogleSearchConsoleData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/gsc/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// Akismet
const getAkismetData = async (
  siteId: string,
  startDate: string,
  endDate: string,
  idToken: string | null
) => {
  return apiFetch(
    `/akismet/${siteId}${buildQuery({ startDate, endDate })}`,
    idToken
  );
};

// Reports
const getReports = async (siteId: string, idToken: string | null) => {
  return apiFetch(`/report/${siteId}`, idToken);
};

// Send Mail
const sendMail = async (
  subject: string,
  html: string,
  idToken: string | null
) => {
  return apiFetch(`/email/send`, idToken, {
    method: "POST",
    body: JSON.stringify({
      from: "APF Reporting Operations <matt@apfome.com>",
      to: "matt@apfome.com",
      subject,
      html,
      tags: [
        { name: "category", value: "feedback" },
        { name: "application", value: "APFReporting" },
      ],
    }),
  });
};

// Get Sites
const getSites = async (clientId: string, idToken: string | null) => {
  return apiFetch(
    `/auth/sites?clientId=${encodeURIComponent(clientId)}`,
    idToken
  );
};

// Get User
const getUser = async (id: string, idToken: string | null) => {
  return apiFetch(`/auth/user?id=${encodeURIComponent(id)}`, idToken);
};

const getInstagramConnection = async (
  userId: string,
  idToken: string | null
) => {
  return apiFetch(
    `/social/instagram/connection?userId=${encodeURIComponent(userId)}`,
    idToken
  );
};

const startInstagramConnection = async (
  userId: string,
  idToken: string | null
) => {
  return apiFetch(
    `/social/instagram/connect?userId=${encodeURIComponent(userId)}`,
    idToken
  );
};

export {
  getGaData,
  getWooData,
  getCampaignsData,
  getWordpressData,
  getSendGridData,
  getGoogleSearchConsoleData,
  getAkismetData,
  getReports,
  sendMail,
  getSites,
  getUser,
  getInstagramConnection,
  startInstagramConnection,
};
